import { notifications } from '@mantine/notifications';
import { IconMoodPuzzled } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { isNetworkError } from '@xver/libs/connectionNotifier';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FirewallSecurityGroupResponse } from '../../../../../@types/firewall-security-group';
import { Endpoints } from '../../../../../apis';

export function useSecurityGroupQuery(groupId: string | undefined) {
    const hasShownError = useRef(false);
    const { t } = useTranslation('cloudComputeSecurityGroup');

    const query = useQuery({
        queryKey: ['api.v1.firewalls.security-groups.single', groupId],
        queryFn: async (): Promise<FirewallSecurityGroupResponse | undefined> => {
            if (!groupId) return undefined;
            // Walk pages until found or exhausted. Most tenants have <100 SGs total.
            for (let page = 1; page <= 100; page++) {
                const res = await Endpoints.v1.Firewalls.SecurityGroups.listSecurityGroups({
                    page,
                    limit: 100,
                });
                const hit = res.rows.find((g) => g.id === groupId);
                if (hit) return hit;
                if (page >= res.total_pages) break;
            }
            return undefined;
        },
        enabled: !!groupId,
        retry: 2,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
        refetchInterval: 30_000,
    });

    const { data, isPending, error, refetch } = query;

    useEffect(() => {
        if (error && !isNetworkError(error) && !hasShownError.current) {
            hasShownError.current = true;
            notifications.show({
                title: t('errors.failedToQuery'),
                message: t('errors.failedToLoadGroup'),
                icon: <IconMoodPuzzled size={32} color={'#eb5757'} />,
            });
        }

        if (!error) {
            hasShownError.current = false;
        }
    }, [error, t]);

    return { data, isPending, refetch };
}
